import { motion } from "framer-motion";
import { Mail, MapPin, Phone } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons";
import { WHATSAPP_URL, PHONE_TEL, PHONE_DISPLAY, MAPS_URL, WHATSAPP2_URL, PHONE2_TEL, PHONE2_DISPLAY, EMAIL, EMAIL_MAILTO } from "@/lib/contact";

const TODAY = new Date().toLocaleDateString("en-IN", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-[#6B1D2F] px-5 py-16 text-[#FAF3E6] lg:py-24">
    <div className="mx-auto max-w-6xl">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6 }}
        className="mb-4 font-mono-brand text-xs font-semibold uppercase tracking-[0.24em] text-[#C5A059]"
      >
        Direct from the farm
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl"
      >
        Talk to <span className="italic text-[#C5A059]">Qaiser</span> or <span className="italic text-[#C5A059]">Venkat</span> — one tap away.
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="mt-4 max-w-lg text-base leading-relaxed text-[#EAD9C2] sm:text-lg"
      >
        Rates change daily with the market. Send a message and we'll reply with
        today's wholesale price and availability.
      </motion.p>
      <motion.p
        data-testid="contact-today-note"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.28 }}
        className="mt-5 font-mono-brand text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C5A059]"
      >
        {TODAY} — rates quoted fresh this morning
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-9 grid gap-5 sm:grid-cols-2"
      >
        <div className="rounded-2xl border border-[#C5A059]/40 bg-[#521422]/50 p-6">
          <p data-testid="contact-qaiser-name" className="font-display text-xl font-semibold italic text-[#C5A059]">Qaiser</p>
          <p className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#EAD9C2]/70">Wholesale orders &amp; rates</p>
          <div className="mt-5 flex flex-col gap-3">
            <motion.a
              data-testid="contact-whatsapp-link"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3.5 text-base font-semibold text-white shadow-[0_14px_34px_rgba(37,211,102,0.35)] transition-colors duration-300 hover:bg-[#1EBE5D]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              WhatsApp
            </motion.a>
            <motion.a
              data-testid="contact-phone-link"
              href={PHONE_TEL}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2.5 rounded-full bg-[#FAF3E6] px-6 py-3.5 text-base font-semibold text-[#6B1D2F] transition-colors duration-300 hover:bg-[#C5A059]"
            >
              <Phone size={18} strokeWidth={2.2} />
              Call {PHONE_DISPLAY}
            </motion.a>
          </div>
        </div>
        <div className="rounded-2xl border border-[#C5A059]/40 bg-[#521422]/50 p-6">
          <p data-testid="contact-venkat-name" className="font-display text-xl font-semibold italic text-[#C5A059]">Venkat</p>
          <p className="mt-1 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#EAD9C2]/70">Wholesale orders &amp; rates</p>
          <div className="mt-5 flex flex-col gap-3">
            <motion.a
              data-testid="contact-venkat-whatsapp-link"
              href={WHATSAPP2_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-6 py-3.5 text-base font-semibold text-white shadow-[0_14px_34px_rgba(37,211,102,0.35)] transition-colors duration-300 hover:bg-[#1EBE5D]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              WhatsApp
            </motion.a>
            <motion.a
              data-testid="contact-venkat-phone-link"
              href={PHONE2_TEL}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2.5 rounded-full bg-[#FAF3E6] px-6 py-3.5 text-base font-semibold text-[#6B1D2F] transition-colors duration-300 hover:bg-[#C5A059]"
            >
              <Phone size={18} strokeWidth={2.2} />
              Call {PHONE2_DISPLAY}
            </motion.a>
          </div>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="mt-6 flex flex-col gap-3 sm:flex-row"
      >
        <motion.a
          data-testid="contact-map-link"
          href={MAPS_URL}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center justify-center gap-2.5 rounded-full border border-[#C5A059]/60 px-8 py-4 text-base font-semibold text-[#EAD9C2] transition-colors duration-300 hover:border-[#C5A059] hover:bg-[#521422]"
        >
          <MapPin size={18} strokeWidth={2.2} />
          Open in Google Maps
        </motion.a>
        <motion.a
          data-testid="contact-email-link"
          href={EMAIL_MAILTO}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="inline-flex items-center justify-center gap-2.5 rounded-full border border-[#C5A059]/60 px-8 py-4 text-base font-semibold text-[#EAD9C2] transition-colors duration-300 hover:border-[#C5A059] hover:bg-[#521422]"
        >
          <Mail size={18} strokeWidth={2.2} />
          {EMAIL}
        </motion.a>
      </motion.div>
    </div>
  </section>
);

export default Contact;
